import {
  type ReactNode,
  type Ref,
  forwardRef,
  useLayoutEffect,
  useState,
} from "react";
import { t } from "ttag";

import { useSetting } from "metabase/settings";
import {
  Button,
  type ButtonProps,
  Icon,
  Loader,
  Menu,
  Tooltip,
} from "metabase/ui";
import type { ColorName } from "metabase/ui/colors/types";
import type {
  TransformId,
  TransformJobId,
  TransformRun,
} from "metabase-types/api";

import { isActiveRunStatus } from "../../utils";
import { LockedTransformsHoverCard } from "../LockedTransformsHoverCard/LockedTransformsHoverCard";

const RECENT_TIMEOUT = 5000;

type RunButtonProps = {
  id: TransformId | TransformJobId | undefined;
  run: TransformRun | null | undefined;
  isDisabled?: boolean;
  isLoading?: boolean;
  allowCancellation?: boolean;
  size?: ButtonProps["size"];
  onRun: () => void;
  onCancel?: () => void;
  menuItems?: ReactNode;
};

export const RunButton = forwardRef(function RunButton(
  {
    id,
    run,
    isDisabled: isExternallyDisabled = false,
    isLoading = false,
    allowCancellation = false,
    size = "md",
    onRun,
    onCancel,
    menuItems,
  }: RunButtonProps,
  ref: Ref<HTMLButtonElement>,
) {
  const isMeterLocked = useSetting("transforms-meter-locked");
  const [isRecent, setIsRecent] = useState(false);
  const { label, color, leftSection, isDisabled } = getRunButtonInfo({
    run,
    isRecent,
    isLoading,
    isDisabled: isExternallyDisabled || !!isMeterLocked,
  });

  useLayoutEffect(() => {
    setIsRecent(true);
    const timeoutId = setTimeout(() => setIsRecent(false), RECENT_TIMEOUT);
    return () => clearTimeout(timeoutId);
  }, [run]);

  useLayoutEffect(() => {
    setIsRecent(false);
  }, [id]);

  const isRunning = isActiveRunStatus(run?.status);
  const showMenu = menuItems != null && !isRunning;

  const runButton = (
    <Button.Group>
      <Button
        ref={ref}
        variant="filled"
        color={color}
        leftSection={leftSection}
        disabled={isDisabled}
        data-testid="run-button"
        size={size}
        onClick={onRun}
      >
        {label}
      </Button>
      {showMenu && (
        <Menu position="bottom-end">
          <Menu.Target>
            <Button
              variant="filled"
              color={color}
              disabled={isDisabled}
              size={size}
              px="sm"
              aria-label={t`More run options`}
              data-testid="run-options-button"
            >
              <Icon name="chevrondown" aria-hidden />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      )}
      {allowCancellation && run?.status === "started" && (
        <Tooltip label={t`Cancel`}>
          <Button
            onClick={onCancel}
            rightSection={<Icon name="close" aria-hidden />}
            data-testid="cancel-button"
          />
        </Tooltip>
      )}
    </Button.Group>
  );

  if (isMeterLocked) {
    return <LockedTransformsHoverCard>{runButton}</LockedTransformsHoverCard>;
  }
  return runButton;
});

type RunButtonOpts = {
  run: TransformRun | null | undefined;
  isRecent: boolean;
  isLoading: boolean;
  isDisabled: boolean;
};

type RunButtonInfo = {
  label: string;
  color?: ColorName;
  leftSection?: ReactNode;
  isDisabled?: boolean;
};

function getRunButtonInfo({
  run,
  isRecent,
  isLoading,
  isDisabled,
}: RunButtonOpts): RunButtonInfo {
  if (run?.status === "started") {
    return {
      label: t`Running now…`,
      leftSection: <Loader size="sm" />,
      isDisabled: true,
    };
  }

  if (run?.status === "canceling") {
    return {
      label: t`Canceling…`,
      leftSection: <Loader size="sm" />,
      color: "text-secondary",
      isDisabled: true,
    };
  }

  if (isLoading) {
    return {
      label: t`Run now`,
      leftSection: <Loader size="sm" />,
      isDisabled: true,
    };
  }

  if (run == null || !isRecent || isDisabled) {
    return {
      label: t`Run now`,
      leftSection: <Icon name="play_outlined" aria-hidden />,
      isDisabled,
    };
  }

  if (run.status === "succeeded") {
    return {
      label: t`Ran successfully`,
      color: "feedback-positive",
      leftSection: <Icon name="check" aria-hidden />,
      isDisabled,
    };
  }

  if (run.status === "canceled") {
    return {
      label: t`Canceled`,
      color: "feedback-warning",
      leftSection: <Icon name="close" c="core-white" aria-hidden />,
      isDisabled,
    };
  }

  return {
    label: t`Run failed`,
    color: "feedback-negative",
    leftSection: <Icon name="warning" aria-hidden />,
    isDisabled,
  };
}
